import React, { useState, useEffect } from 'react'
import { useSearchParams, useLocation } from 'react-router-dom'
import { Building2, MapPin, Users, Settings as SettingsIcon, Cog } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import BrandList from '@/pages/brands/BrandList'
import AreaList from '@/pages/areas/AreaList'
import UserList from '@/pages/users/UserList'
import SystemSettings from '@/pages/settings/SystemSettings'

const Settings = () => {
  const { hasRole } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  // Determine default tab based on current route
  const getDefaultTab = () => {
    if (location.pathname.includes('/brands')) return 'brands'
    if (location.pathname.includes('/areas')) return 'areas'
    if (location.pathname.includes('/users')) return 'users'
    return 'brands'
  }

  const activeTab = searchParams.get('tab') || getDefaultTab()

  // Define available tabs based on user role
  const getAvailableTabs = () => {
    const tabs = []

    // Admin: Access to all tabs including system settings
    if (hasRole('admin')) {
      tabs.push(
        { id: 'brands', name: 'Brands', icon: Building2, component: BrandList },
        { id: 'areas', name: 'Areas', icon: MapPin, component: AreaList },
        { id: 'users', name: 'Users', icon: Users, component: UserList },
        { id: 'system', name: 'System Settings', icon: Cog, component: SystemSettings }
      )
    }
    // Manager: Access to Brands, Users and Areas tabs within their brand scope
    else if (hasRole('manager')) {
      tabs.push(
        { id: 'brands', name: 'Brands', icon: Building2, component: BrandList },
        { id: 'areas', name: 'Areas', icon: MapPin, component: AreaList },
        { id: 'users', name: 'Users', icon: Users, component: UserList }
      )
    }
    // Area Manager: Access to Settings menu, read and edit in areas tab, read only in brands tab
    else if (hasRole('area_manager')) {
      tabs.push(
        { id: 'brands', name: 'Brands', icon: Building2, component: BrandList, readOnly: true },
        { id: 'areas', name: 'Areas', icon: MapPin, component: AreaList }
      )
    }
    // User: Read-only access to Settings menu for brand and areas tabs
    else if (hasRole('user')) {
      tabs.push(
        { id: 'brands', name: 'Brands', icon: Building2, component: BrandList, readOnly: true },
        { id: 'areas', name: 'Areas', icon: MapPin, component: AreaList, readOnly: true }
      )
    }

    return tabs
  }

  const availableTabs = getAvailableTabs()
  const currentTab = availableTabs.find(tab => tab.id === activeTab) || availableTabs[0]

  const handleTabChange = (tabId) => {
    setSearchParams({ tab: tabId })
  }

  // Add loading state for better UX
  const [isTabLoading, setIsTabLoading] = useState(false)

  const handleTabChangeWithLoading = (tabId) => {
    setIsTabLoading(true)
    handleTabChange(tabId)
    // Reset loading state after a short delay
    setTimeout(() => setIsTabLoading(false), 100)
  }

  // If user has no access to settings, show access denied
  if (availableTabs.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <SettingsIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            You don't have permission to access settings.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <SettingsIcon className="mr-3 h-6 w-6" />
          Settings
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage your system configuration and data
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {availableTabs.map((tab) => {
                const Icon = tab.icon
                const isActive = tab.id === activeTab
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChangeWithLoading(tab.id)}
                    disabled={isTabLoading}
                    className={`
                      group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200
                      ${isActive
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                      ${isTabLoading ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <Icon className={`
                      -ml-0.5 mr-2 h-5 w-5
                      ${isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}
                    `} />
                    {tab.name}
                  </button>
                )
              })}
            </nav>
          </div>

        {/* Tab Content */}
        <div className="p-6">
          {isTabLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : currentTab ? (
            <div className="transition-opacity duration-200">
              <currentTab.component />
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No content available for this tab.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Settings
